// Google Analytics 4, driven entirely by JSON the server renders.
//
// No page carries an inline gtag snippet. The server decides whether the tag is
// live (Analytics::GoogleTag: the laborlodge.com host, the storefront's
// organization) and renders:
//   <script type="application/json" data-analytics-page>   where this page is
//   <script type="application/json" data-analytics-events> what just happened
// This file loads gtag.js only when the first of those is present.
//
// What reaches Google is deliberately narrower than what gtag collects by
// default. Guest links carry bearer tokens in their URLs, so page_location is
// the route pattern plus campaign parameters, page_referrer is an origin, and
// both are set before gtag sends anything.
//
// Built as its own entry (app/assets/builds/analytics.js) for the marketing
// pages, and imported by application.js for everything else.

const PAGE_SELECTOR = 'script[type="application/json"][data-analytics-page]'
const EVENTS_SELECTOR = 'script[type="application/json"][data-analytics-events]'
const CAMPAIGN_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id",
  "gclid", "gbraid", "wbraid", "msclkid", "fbclid"
]

const sentEventIds = new Set()
let measurementId = null
let surface = null
let previousLocation = null
let configured = false

function readJson(element) {
  try {
    return JSON.parse(element.textContent)
  } catch {
    return null
  }
}

function gtag() {
  window.dataLayer.push(arguments)
}

function loadGtag(id) {
  if (measurementId) return measurementId === id

  measurementId = id
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || gtag
  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(script)
  window.gtag("js", new Date())
  return true
}

function sanitizedLocation(pagePath) {
  const kept = new URLSearchParams()
  const current = new URLSearchParams(window.location.search)
  CAMPAIGN_PARAMS.forEach((name) => {
    const value = current.get(name)
    if (value) kept.set(name, value.slice(0, 200))
  })
  const query = kept.toString()
  return `${window.location.origin}${pagePath || window.location.pathname}${query ? `?${query}` : ""}`
}

// Within the site, the previous page as already sanitized (Turbo visits) or
// just the origin (full loads). From elsewhere, only the other site's origin:
// a referring URL can carry someone's link.
function sanitizedReferrer() {
  if (previousLocation) return previousLocation.split("?")[0]
  if (!document.referrer) return undefined
  try {
    return `${new URL(document.referrer).origin}/`
  } catch {
    return undefined
  }
}

function trackPage() {
  const element = document.querySelector(PAGE_SELECTOR)
  const page = element && readJson(element)
  if (!page || !page.measurement_id || !loadGtag(page.measurement_id)) return

  surface = page.surface || null
  const location = sanitizedLocation(page.page_path)
  const context = { page_location: location, page_title: page.page_title || document.title }
  const referrer = sanitizedReferrer()
  if (referrer) context.page_referrer = referrer

  window.gtag("set", context)
  if (!configured) {
    configured = true
    window.gtag("config", measurementId, { ...context, send_page_view: false })
  }
  window.gtag("event", "page_view", { ...context, surface })
  previousLocation = location
}

function sendEvents(root = document) {
  if (!measurementId) return

  root.querySelectorAll(EVENTS_SELECTOR).forEach((element) => {
    const events = readJson(element)
    element.remove()
    if (!Array.isArray(events)) return

    events.forEach((event) => {
      if (!event || !event.id || !event.name || sentEventIds.has(event.id)) return
      sentEventIds.add(event.id)
      window.gtag("event", event.name, { ...(event.params || {}), surface })
    })
  })
}

// Calling and texting are how crews book; count the intent, not the number.
function trackContactClick(event) {
  if (!measurementId) return
  const link = event.target.closest && event.target.closest('a[href^="tel:"], a[href^="sms:"], a[href^="mailto:"]')
  if (!link) return

  const method = link.protocol.replace(":", "")
  window.gtag("event", method === "tel" ? "click_to_call" : "contact_click", { method, surface })
}

function run() {
  trackPage()
  sendEvents()
}

if (!window.__laborlodgeAnalytics) {
  window.__laborlodgeAnalytics = true
  document.addEventListener("click", trackContactClick, { capture: true })
  document.addEventListener("turbo:frame-load", (event) => sendEvents(event.target))
  if (window.Turbo) {
    document.addEventListener("turbo:load", run)
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true })
  } else {
    run()
  }
}
